import React, { useState, useEffect } from "react";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { common } from "../util/common";
import { useNavigate } from "react-router-dom";
import { buildConversationPath } from "../util/helperFunctions";
import ModalShell from "./modal/ModalShell";
import ImageUploader from "./modal/ImageUploader";
import UserPicker from "./modal/UserPicker";

const NAME_MAX = 100;

function ConversationModal(props) {
  const navigate = useNavigate();
  const { showHideConversationModal } = props;
  const [conversationName, setConversationName] = useState("");
  const [members, setMembers] = useState([]); // full user objects, single source of truth
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [imageData, setImageData] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchUserNames = async () => {
      try {
        const response = await ApiService.users.getAllusers();
        const { success, users } = response;
        if (cancelled) return;
        if (success) setAllUsers(users);
        else showSwal(common.errors.CONVERSATION_FETCH_USERNAME_ERROR);
      } catch (error) {
        console.error("Error fetching user names:", error);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };

    fetchUserNames();
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedName = conversationName.trim();
  const canSubmit = trimmedName.length > 0 && !formSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setFormSubmitting(true);

    try {
      const formData = new FormData();
      const payload = {
        name: trimmedName,
        conversation_member_ids: members.map((m) => m.id),
        is_group: true,
      };

      formData.append("conversation_image", imageData);
      formData.append("data", JSON.stringify(payload));

      const _conversation = await ApiService.conversation.createConversation(formData);
      const { success, conversation_id } = _conversation;

      if (success) {
        showHideConversationModal();
        showSwal(common.success.CONVERSATION_CREATED).then(() => {
          navigate(buildConversationPath(conversation_id));
        });
        return;
      }
      showSwal(common.errors.CREATE_CONVERSATION_ERROR);
    } catch (err) {
      console.error(err);
      showSwal(common.errors.CREATE_CONVERSATION_ERROR);
    }
    setFormSubmitting(false); // failure: keep the form so the user can retry
  };

  return (
    <ModalShell
      title='New group conversation'
      subtitle='Set a name and photo, then add people'
      size='md'
      onClose={showHideConversationModal}
      footer={
        <>
          <button type='button' className='bh-btn bh-btn--ghost' onClick={showHideConversationModal} disabled={formSubmitting}>
            Cancel
          </button>
          <button type='submit' form='bh-create-conversation-form' className='bh-btn bh-btn--primary' disabled={!canSubmit}>
            {formSubmitting && <span className='bh-spinner' />}
            {formSubmitting ? "Creating…" : "Create conversation"}
          </button>
        </>
      }
    >
      <form id='bh-create-conversation-form' onSubmit={handleSubmit}>
        <div className='bh-field'>
          <span className='bh-label'>Conversation image</span>
          <ImageUploader file={imageData} onChange={setImageData} disabled={formSubmitting} />
        </div>
        <div className='bh-field'>
          <div className='bh-field__row'>
            <label className='bh-label' htmlFor='bh-create-name'>
              Conversation name
            </label>
            <span className='bh-hint' style={{ marginTop: 0 }}>
              {conversationName.length}/{NAME_MAX}
            </span>
          </div>
          <input
            id='bh-create-name'
            data-autofocus
            type='text'
            className='bh-input'
            value={conversationName}
            maxLength={NAME_MAX}
            placeholder='e.g. Project Alpha team'
            onChange={(e) => setConversationName(e.target.value)}
            disabled={formSubmitting}
            required
          />
        </div>
        <div className='bh-field'>
          <div className='bh-section-title'>
            <span>Members</span>
            <span className='bh-hint' style={{ margin: 0 }}>
              {members.length} selected
            </span>
          </div>
          <UserPicker
            users={allUsers}
            selected={members}
            loading={usersLoading}
            disabled={formSubmitting}
            onAdd={(u) => setMembers((prev) => (prev.some((m) => m.id === u.id) ? prev : [...prev, u]))}
            onRemove={(u) => setMembers((prev) => prev.filter((m) => m.id !== u.id))}
          />
        </div>
      </form>
    </ModalShell>
  );
}

export default ConversationModal;
